import { useEffect, useState } from 'react'
import { getWallboxOcppSettings, updateWallboxOcppSettings } from '../api/wallboxes.api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { WallboxOcppModel } from '../models'
import { WallboxOcppDto, WallboxType } from '../dto'
import { ApiResult, usePortalMutation, usePortalQuery } from 'src/Libs/api'

interface ReturnType {
  ocppSettings: WallboxOcppModel | null
  updateOcppSettings: (settings: WallboxOcppDto) => Promise<void>
  isLoading: boolean
  error: Error | null
}

/**
 * This hook provides the OCPP settings for a wallbox.
 *
 * @param storageId - Storage ID of the SSI or farm client.
 * @param wallboxId
 */
const useWallboxOcppSettings = (storageId?: string, wallboxId?: string, wallboxType?: number): ReturnType => {
  const [ocppSettings, setOcppSettings] = useState<WallboxOcppModel | null>(null)
  const authFetch = useAuthFetchWithResponse()
  const isFetchingEnabled = storageId !== undefined && wallboxId !== undefined && wallboxType === WallboxType.Efy

  const { data, isLoading, error, refetch } = usePortalQuery({
    queryKey: ['wallboxOcppSettings', storageId, wallboxId],
    queryFn: async () => await getWallboxOcppSettings(storageId, wallboxId, authFetch),
    enabled: isFetchingEnabled,
    retry: 1,
  })

  const { mutateAsync: updateOcppSettingsMutation } = usePortalMutation({
    mutationKey: ['updateWallboxOcppSettings', storageId, wallboxId],
    mutationFn: async (settings: WallboxOcppDto) => new ApiResult(await updateWallboxOcppSettings(storageId, wallboxId, settings, authFetch)),
  })

  useEffect(() => {
    if (data && !isLoading && !error) {
      setOcppSettings(data)
    }
  }, [data, isLoading, error])

  const updateOcppSettings = async (settings: WallboxOcppDto): Promise<void> => {
    try {
      await updateOcppSettingsMutation(settings)
      await refetch()
    } catch (error) {
      console.error('Failed to update OCPP settings:', error)
    }
  }

  return { ocppSettings, updateOcppSettings, isLoading, error }
}

export default useWallboxOcppSettings
